import { Panel, Container, TreeViewItem, TreeView, Button, Label } from '@playcanvas/pcui/react';
import React from 'react';

import { addEventListenerOnClickOnly } from '../../helpers';
import { HierarchyNode, SetProperty, ObserverData } from '../../types';
import { Detail, Select, Toggle, Vector } from '../components';
import MorphTargetPanel from './morph-target-panel';
import AnimationPanel from './animation-panel';
import AnimationControlsPanel from './animation-controls-panel';
import AssetsPanel from './assets-panel';
import AircraftConfigPanel from './aircraft-config-panel';
import LiveriesPanel from './liveries-panel';

declare global {
    interface Navigator {
      readonly gpu: any;
    }
}

const toggleCollapsed = () => {
    const leftPanel = document.getElementById('panel-left');
    if (leftPanel) {
        leftPanel.classList.toggle('collapsed');
    }
};

let leftPanel: any;
const openPanel = () => {
    if (!leftPanel) {
        leftPanel = document.getElementById('panel-left');
    }
    if (leftPanel && leftPanel.classList.contains('collapsed')) {
        leftPanel.classList.remove('collapsed');
    }
};

const bytesToSizeString = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return 'n/a';
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
    return (i === 0) ? `${bytes} ${sizes[i]}` : `${(bytes / (1024 ** i)).toFixed(1)} ${sizes[i]}`;
};

class ManifestPanel extends React.Component <{ 
    manifestData: ObserverData['manifestData'],
    aircraftConfigData: ObserverData['aircraftConfig'],
    setProperty: SetProperty,
    isInIframe?: boolean
}> {
    shouldComponentUpdate(nextProps: Readonly<{ 
        manifestData: ObserverData['manifestData'];
        aircraftConfigData: ObserverData['aircraftConfig'];
        setProperty: SetProperty; 
    }>): boolean {
        return JSON.stringify(nextProps.manifestData) !== JSON.stringify(this.props.manifestData) ||
               JSON.stringify(nextProps.aircraftConfigData) !== JSON.stringify(this.props.aircraftConfigData);
    }

    // Find matching package by ID from aircraft config
    findMatchingConfigPackage(manifestData: any, aircraftConfigData: any) {
        if (!manifestData?.loaded || !manifestData?.manifest || !aircraftConfigData?.loaded || !aircraftConfigData?.configuration) {
            return null;
        }

        const manifest = manifestData.manifest;
        const config = aircraftConfigData.configuration;
        
        // Get the ID from the aircraft configuration (try both camelCase and PascalCase)
        const configId = config.id || config.ID;
        
        if (!configId) {
            return null;
        }

        // Search through model packages first (they can also represent aircraft)
        if (manifest.modelPackages) {
            for (const modelPkg of manifest.modelPackages) {
                if (modelPkg.id && modelPkg.id.toLowerCase() === configId.toLowerCase()) {
                    return modelPkg;
                }
            }
        }

        // Search through configuration packages
        if (manifest.configurationPackages) {
            for (const configPkg of manifest.configurationPackages) {
                if (configPkg.id && configPkg.id.toLowerCase() === configId.toLowerCase()) {
                    return configPkg;
                }
            }
        }
        
        return null;
    }

    // Find all livery packages that reference this model ID
    findMatchingLiveries(manifestData: any, modelId: string) {
        if (!manifestData?.loaded || !manifestData?.manifest || !modelId) {
            return [];
        }

        const manifest = manifestData.manifest;
        const matchingLiveries: any[] = [];

        // Search through livery packages
        if (manifest.liveryPackages) {
            for (const liveryPkg of manifest.liveryPackages) {
                if (liveryPkg.modelID && liveryPkg.modelID.toLowerCase() === modelId.toLowerCase()) {
                    matchingLiveries.push(liveryPkg);
                }
            }
        }

        return matchingLiveries;
    }

    render() {
        const manifestData = this.props.manifestData;
        const aircraftConfigData = this.props.aircraftConfigData;
        
        // Calculate total package count
        let totalPackages = 0;
        let importDate = 'No manifest imported';
        let matchedConfig = null;
        let matchingLiveries: any[] = [];
        
        if (manifestData?.loaded && manifestData?.manifest) {
            const manifest = manifestData.manifest;
            totalPackages = (manifest.modelPackages?.length || 0) +
                          (manifest.liveryPackages?.length || 0) +
                          (manifest.configurationPackages?.length || 0) +
                          (manifest.soundPackages?.length || 0) +
                          (manifest.sharedPackages?.length || 0);
            
            if (manifestData.importedAt) {
                importDate = new Date(manifestData.importedAt).toLocaleString();
            }
            
            // Find matching configuration package by ID
            matchedConfig = this.findMatchingConfigPackage(manifestData, aircraftConfigData);
            
            // If we found a config package, find all liveries that reference it
            if (matchedConfig && matchedConfig.id) {
                matchingLiveries = this.findMatchingLiveries(manifestData, matchedConfig.id);
            }
        }

        // Hide the import button if we're in an iframe (manifest is loaded from API)
        const showImportButton = !this.props.isInIframe;

        return (
            <Panel headerText='MANIFEST' id='manifest-panel' flexShrink={'0'} flexGrow={'0'} collapsible={true} collapsed={true}>
                {showImportButton && (
                    <Button
                        class='secondary'
                        text='IMPORT PACKAGE MANIFEST'
                        onClick={() => {
                            if (window.viewer) window.viewer.importManifest();
                        }}
                    />
                )}
                
                {manifestData?.loaded && manifestData?.manifest && (
                    <div className='manifest-info-section'>
                        <Label text='MANIFEST INFO' class={['manifest-info-title']} />
                        <div className='manifest-info-row'>
                            <Label text='Last Import:' class={['manifest-info-label']} />
                            <Label text={importDate} class={['manifest-info-value']} />
                        </div>
                        <div className='manifest-info-row'>
                            <Label text='Total Packages:' class={['manifest-info-label']} />
                            <Label text={totalPackages.toString()} class={['manifest-info-value', 'highlight']} />
                        </div>
                        {matchedConfig && (
                            <>
                                <div className='manifest-divider' />
                                <div className='manifest-info-row'>
                                    <Label text='Current Aircraft:' class={['manifest-info-label']} />
                                    <Label text={matchedConfig.name} class={['manifest-info-value', 'highlight']} />
                                </div>
                                <div className='manifest-info-row'>
                                    <Label text='Package ID:' class={['manifest-info-label']} />
                                    <Label text={matchedConfig.id.substring(0, 8) + '...'} class={['manifest-info-value']} />
                                </div>
                                <div className='manifest-info-row'>
                                    <Label text='Version:' class={['manifest-info-label']} />
                                    <Label text={matchedConfig.version.toString()} class={['manifest-info-value']} />
                                </div>
                                <div className='manifest-info-row'>
                                    <Label text='Liveries Available:' class={['manifest-info-label']} />
                                    <Label text={matchingLiveries.length.toString()} class={['manifest-info-value', 'highlight']} />
                                </div>
                            </>
                        )}
                        {!matchedConfig && aircraftConfigData?.loaded && aircraftConfigData?.configuration && (
                            <>
                                <div className='manifest-divider' />
                                <div className='manifest-info-row'>
                                    <Label text='Current Aircraft:' class={['manifest-info-label']} />
                                    <Label text='Not found in manifest' class={['manifest-info-value', 'warning']} />
                                </div>
                            </>
                        )}
                    </div>
                )}
            </Panel>
        );
    }
}

class ScenePanel extends React.Component <{ sceneData: ObserverData['scene'], setProperty: SetProperty }> {
    shouldComponentUpdate(nextProps: Readonly<{ sceneData: ObserverData['scene']; setProperty: SetProperty; }>): boolean {
        return (
            JSON.stringify(nextProps.sceneData.filenames) !== JSON.stringify(this.props.sceneData.filenames) ||
            nextProps.sceneData.loadTime !== this.props.sceneData.loadTime ||
            nextProps.sceneData.meshCount !== this.props.sceneData.meshCount ||
            nextProps.sceneData.vertexCount !== this.props.sceneData.vertexCount ||
            nextProps.sceneData.primitiveCount !== this.props.sceneData.primitiveCount ||
            nextProps.sceneData.materialCount !== this.props.sceneData.materialCount ||
            nextProps.sceneData.textureVRAM !== this.props.sceneData.textureVRAM ||
            nextProps.sceneData.meshVRAM !== this.props.sceneData.meshVRAM ||
            nextProps.sceneData.bounds !== this.props.sceneData.bounds ||
            nextProps.sceneData.variant.selected !== this.props.sceneData.variant.selected ||
            nextProps.sceneData.variants.list !== this.props.sceneData.variants.list
        );
    }

    render() {
        const scene = this.props.sceneData;
        const variantListOptions: Array<{ v:string, t:string }> = JSON.parse(scene.variants.list).map((variant: string) => ({ v: variant, t: variant }));
        return (
            <Panel headerText='SCENE' id='scene-panel' flexShrink={'0'} flexGrow={'0'} collapsible={true} collapsed={true}>
                <Detail label='Filename' value={scene.filenames.join(', ')} />
                <Detail label='Meshes' value={scene.meshCount} />
                <Detail label='Materials' value={scene.materialCount} />
                <Detail label='Textures' value={scene.textureCount} />
                <Detail label='Primitives' value={scene.primitiveCount} />
                <Detail label='Verts' value={scene.vertexCount} />
                <Detail label='Mesh VRAM' value={bytesToSizeString(scene.meshVRAM)} />
                <Detail label='Texture VRAM' value={bytesToSizeString(scene.textureVRAM)} />
                <Detail label='Load time' value={scene.loadTime} />
                <Vector label='Bounds' dimensions={3} value={scene.bounds} enabled={false}/>
                <Select label='Variant' type='string' options={variantListOptions} value={scene.variant.selected}
                    setProperty={(value: string) => {
                        this.props.setProperty('scene.variant.selected', value);
                    }}
                    enabled={ variantListOptions.length > 0 }
                />
            </Panel>
        );
    }
}

class HierarchyPanel extends React.Component <{ sceneData: ObserverData['scene'], setProperty: SetProperty }> {
    shouldComponentUpdate(nextProps: Readonly<{ sceneData: ObserverData['scene']; setProperty: SetProperty; }>): boolean {
        return (
            nextProps.sceneData.nodes !== this.props.sceneData.nodes
        );
    }

    render() {
        const scene = this.props.sceneData;
        const modelHierarchy: Array<HierarchyNode> = JSON.parse(scene.nodes);
        const mapNodes = (nodes: Array<HierarchyNode>) => {
            return nodes.map((node:HierarchyNode) => <TreeViewItem text={`${node.name}`} key={node.path}
                onSelect={(TreeViewItem: any) => {
                    this.props.setProperty('scene.selectedNode.path', node.path);
                    const removeEventListener = addEventListenerOnClickOnly(document.body, () => {
                        TreeViewItem.selected = false;
                        removeEventListener();
                    }, 4);
                }}
                onDeselect={() => this.props.setProperty('scene.selectedNode.path', '')}
            >
                { mapNodes(node.children) }
            </TreeViewItem>);
        };
        return (
            <Panel headerText='HIERARCHY' class='scene-hierarchy-panel' enabled={modelHierarchy.length > 0} collapsible={true} collapsed={true}>
                { modelHierarchy.length > 0 &&
                    <TreeView allowReordering={false} allowDrag={false}>
                        { mapNodes(modelHierarchy) }
                    </TreeView>
                }
            </Panel>
        );
    }
}

class DevicePanel extends React.Component <{ observerData: ObserverData, setProperty: SetProperty }> {
    shouldComponentUpdate(nextProps: Readonly<{ observerData: ObserverData, setProperty: SetProperty}>): boolean {
        return JSON.stringify(nextProps.observerData.runtime) !== JSON.stringify(this.props.observerData.runtime) ||
               nextProps.observerData.enableWebGPU !== this.props.observerData.enableWebGPU;
    }

    render() {
        const runtime = this.props.observerData.runtime;
        return (
            <Panel headerText='DEVICE' id='device-panel' collapsible={false}>
                <Detail label='Viewport' value={`${runtime.viewportWidth} x ${runtime.viewportHeight}`} />
            </Panel>
        );
    }
}

class LeftPanel extends React.Component <{ observerData: ObserverData, setProperty: SetProperty, isInIframe?: boolean }> {
    isMobile: boolean;

    constructor(props: any) {
        super(props);
        this.isMobile = (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    }

    shouldComponentUpdate(nextProps: Readonly<{ observerData: ObserverData; setProperty: SetProperty; }>): boolean {
        return JSON.stringify(nextProps.observerData.scene) !== JSON.stringify(this.props.observerData.scene) ||
               JSON.stringify(nextProps.observerData.runtime) !== JSON.stringify(this.props.observerData.runtime) ||
               JSON.stringify(nextProps.observerData.manifestData) !== JSON.stringify(this.props.observerData.manifestData) ||
               JSON.stringify(nextProps.observerData.aircraftConfig) !== JSON.stringify(this.props.observerData.aircraftConfig) ||
               JSON.stringify(nextProps.observerData.liveries) !== JSON.stringify(this.props.observerData.liveries);
    }

    componentDidMount(): void {
        // set up the control panel toggle button
        document.getElementById('panel-toggle').addEventListener('click', () => {
            toggleCollapsed();
        });
        document.getElementById('title').addEventListener('click', () => {
            toggleCollapsed();
        });
        // we require this setTimeout because panel isn't yet created and so fails
        // otherwise.
        setTimeout(() => toggleCollapsed());
    }

    componentDidUpdate(prevProps: Readonly<{ observerData: ObserverData; setProperty: SetProperty; }>): void {
        if (!this.isMobile &&
            !this.props.observerData.ui.fullscreen &&
             this.props.observerData.scene.nodes !== '[]' &&
             prevProps.observerData.scene.nodes === '[]') {
            openPanel();
        }
    }

    render() {
        const scene = this.props.observerData.scene;
        const morphs = this.props.observerData.morphs;
        const animations = this.props.observerData.animations || {};
        const animationData = this.props.observerData.animation;
        const liveries = this.props.observerData.liveries;
        return (
            <Container id='scene-container' flex>
                <ManifestPanel 
                    manifestData={this.props.observerData.manifestData} 
                    aircraftConfigData={this.props.observerData.aircraftConfig}
                    setProperty={this.props.setProperty}
                    isInIframe={this.props.isInIframe}
                />
                <ScenePanel sceneData={scene} setProperty={this.props.setProperty} />
                <div id='scene-scrolly-bits'>
                    <AnimationPanel 
                        animationData={animationData}
                        animations={animations}
                        setProperty={this.props.setProperty} 
                    />
                    <AnimationControlsPanel 
                        observerData={this.props.observerData}
                        setProperty={this.props.setProperty} 
                    />
                    <LiveriesPanel liveriesData={liveries} setProperty={this.props.setProperty} />
                    <HierarchyPanel sceneData={scene} setProperty={this.props.setProperty} />
                    <AssetsPanel observerData={this.props.observerData} setProperty={this.props.setProperty} />
                    <AircraftConfigPanel aircraftConfigData={this.props.observerData.aircraftConfig} setProperty={this.props.setProperty} />
                    <MorphTargetPanel progress={this.props.observerData.animation.progress} morphs={morphs} setProperty={this.props.setProperty} />
                </div>
                <DevicePanel observerData={this.props.observerData} setProperty={this.props.setProperty} />
            </Container>
        );
    }
}

export default LeftPanel;
